"use client";

import { useEffect } from "react";
import { usePathname, useParams } from "next/navigation";
import Intercom from "@intercom/messenger-js-sdk";
import useUserInfo from "@/hooks/useUserInfo";

const APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

// "/login-m" is the superSuperAdmin impersonation login - same auth surface as "/login",
// so it must skip /api/user/me too (otherwise: 401 → refresh fail → logout → redirect to /login).
// /tech-boxscore: the Tech BoxScore wizard and guide, both reached before sign in.
// /tv: the public TV board (share link + code, no login); a 401 there would bounce the TV to /login.
const AUTH_PATHS = ["/login", "/login-m", "/register", "/forgot-password", "/reset-password", "/self-onboarding", "/tech-boxscore", "/tv"];

export function isAuthPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return AUTH_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"));
}

// Wall-mounted TV board: no chat launcher at all, not even as a visitor.
function isTvPath(pathname: string | null): boolean {
  return !!pathname && pathname.startsWith("/tv/");
}

export default function IntercomProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const params = useParams();
  const onAuthRoute = isAuthPath(pathname);
  const onTvRoute = isTvPath(pathname);
  // Hide the default launcher on pages with companyId (sidebar has a Support button there)
  const hasCompanyId = Boolean(params?.companyId);
  // Skip /api/user/me on auth routes to avoid 401 → redirect loop; useUserInfo accepts optional { enabled }
  const { userId, userName, email, createdAt, loading } = useUserInfo(
    { enabled: !onAuthRoute } as Parameters<typeof useUserInfo>[0],
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (!APP_ID) return;
    if (onTvRoute) return;

    // On login/register etc. boot as visitor immediately so we never call /api/user/me (avoids 401 → redirect loop)
    if (onAuthRoute) {
      Intercom({ app_id: APP_ID });
      return;
    }

    const initIntercom = async () => {
      if (loading) return;

      if (userId && userName != null && email) {
        // Logged-in user: boot with user data; optionally add JWT for identity verification
        const basePayload: Parameters<typeof Intercom>[0] = {
          app_id: APP_ID,
          user_id: userId,
          name: userName,
          email,
          hide_default_launcher: hasCompanyId,
          ...(createdAt != null && { created_at: createdAt }),
        };

        try {
          const res = await fetch("/api/intercom/identity");
          if (res.ok) {
            const { jwt } = await res.json();
            Intercom({
              ...basePayload,
              api_base: "https://api-iam.intercom.io",
              intercom_user_jwt: jwt,
            });
          } else {
            Intercom(basePayload);
          }
        } catch {
          Intercom(basePayload);
        }
      } else {
        // Anonymous visitor (e.g. other public pages)
        Intercom({ app_id: APP_ID });
      }
    };

    initIntercom();
  }, [onAuthRoute, onTvRoute, userId, userName, email, createdAt, loading, hasCompanyId]);

  return <>{children}</>;
}
